/** REACT_APP_USE_VERSION_POLICY=true → CheckClientVersion, 그 외 → REACT_APP_USE_URL 만 */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import Commons from './Common';
import Global from './Global';
import { logApiUrl } from './logApiUrl';
import {
  getLiveUpdateRequestInfo,
  handleLiveUpdate,
  isLiveUpdateEnabled,
} from './LiveUpdate';

/**
 * 웹(PWA)·브라우저 테스트용 버전.
 * 네이티브 앱은 설치된 스토어 앱 버전(android versionName, ios MARKETING_VERSION)을 직접 읽어서 보냄
 * — OTA 로 웹 번들만 바뀌어도 서버에는 실제 스토어 앱 버전이 가야 스토어 업데이트·심사 판단이 맞음
 */
const APP_RELEASE = {
  IOS: { currentVersion: 'v1.4.2', buildNo: '' },
  ANDROID: { currentVersion: 'v1.4.2', buildNo: '' },
};

/** 브라우저(macStart)에서 테스트할 플랫폼 — IOS | ANDROID */
const DEV_PLATFORM = 'ANDROID';

/** 앱으로 돌아왔을 때 OTA 재확인 간격 */
const LIVE_UPDATE_RECHECK_MS = 30 * 60 * 1000;

let policy = null;
let lastLiveUpdateCheckAt = 0;

export const getClientVersionPolicy = () => policy;

/** 'v1.3.9' 형식으로 맞춤 — Android versionName "1.3.9.0" 의 끝 .0 은 떼어 서버 정책(VERSION) 형식과 같게 */
const toPolicyVersion = (version) => {
  const parts = String(version || '')
    .trim()
    .replace(/^v/i, '')
    .split('.')
    .filter(Boolean);
  while (parts.length > 3 && parts[parts.length - 1] === '0') parts.pop();
  return parts.length ? `v${parts.join('.')}` : '';
};

const getClientVersionInfo = async () => {
  const cap = Capacitor.getPlatform();
  const platform =
    cap === 'ios' ? 'IOS' : cap === 'android' ? 'ANDROID' : DEV_PLATFORM;
  const release = APP_RELEASE[platform] || APP_RELEASE.ANDROID;
  let currentVersion = release.currentVersion;

  if (Capacitor.isNativePlatform()) {
    try {
      const info = await App.getInfo();
      currentVersion = toPolicyVersion(info.version) || currentVersion;
    } catch (e) {
      console.warn('[ApiUrl] 앱 버전 조회 실패 — APP_RELEASE 사용', e);
    }
  }

  return { platform, currentVersion, buildNo: release.buildNo };
};

const getEnvBaseUrl = () => {
  let base = (process.env.REACT_APP_USE_URL || '').trim();
  if (base && !base.endsWith('/')) base += '/';
  return base;
};

const postClientVersionCheck = async (checkUrl, payload) => {
  const res = await Commons.Axios('post', checkUrl, payload);

  const body = res?.data ?? res;
  if (body?.type === 'Error') throw new Error(body.message);

  const row = body.data?.[0];
  if (!row) throw new Error('CheckClientVersion 응답 없음');
  return row;
};

export const shouldShowUpdateDialog = () => {
  if (!Capacitor.isNativePlatform()) return false;
  if (process.env.REACT_APP_USE_VERSION_POLICY !== 'true' || !policy)
    return false;
  if (policy.forceUpdate) return true;

  const cur = Commons.Storages('get', 'version')?.version || '';
  const n = (v) =>
    String(v || '')
      .replace(/^v/i, '')
      .split('.')
      .map((x) => +x || 0);
  const cmp = (a, b) => {
    const x = n(a);
    const y = n(b);
    for (let i = 0; i < Math.max(x.length, y.length); i++) {
      if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) - (y[i] ?? 0);
    }
    return 0;
  };

  if (policy.minVersion && cmp(cur, policy.minVersion) < 0) return true;
  if (policy.latestVersion && cmp(cur, policy.latestVersion) < 0) return true;
  return false;
};

export async function initClientVersionPolicy() {
  logApiUrl('init 시작', {
    USE_VERSION_POLICY: process.env.REACT_APP_USE_VERSION_POLICY,
    env_USE_URL: process.env.REACT_APP_USE_URL,
    globalInit: Global.base.useUrl,
  });

  const { platform, currentVersion, buildNo } = await getClientVersionInfo();

  Commons.Storages('set', 'version', { version: currentVersion });

  const base = getEnvBaseUrl();

  if (process.env.REACT_APP_USE_VERSION_POLICY !== 'true') {
    policy = null;
    Commons.Storages('remove', 'clientVersionPolicy');
    if (base) Global.base.useUrl = base;
    logApiUrl('버전정책 OFF', { finalBaseUrl: Global.base.useUrl });
    return null;
  }

  if (!base) {
    console.warn('[ApiUrl] REACT_APP_USE_URL 없음');
    return null;
  }

  const checkUrl = `${base}${Global.urls.Account.CheckClientVersion}`;
  logApiUrl('CheckClientVersion 요청', {
    checkUrl,
    platform,
    currentVersion,
    buildNo,
    baseBefore: Global.base.useUrl,
  });

  let ota = null;
  lastLiveUpdateCheckAt = Date.now();

  try {
    const row = await postClientVersionCheck(checkUrl, {
      platform,
      currentVersion,
      buildNo,
      ...(await getLiveUpdateRequestInfo()),
    });
    ota = row.ota ?? null;

    let apiUrl = (row.apiBaseUrl || '').trim();
    if (apiUrl && !apiUrl.endsWith('/')) apiUrl += '/';

    logApiUrl('CheckClientVersion 응답', {
      responseApiBaseUrl: row.apiBaseUrl,
      forceUpdate: row.forceUpdate,
      latestVersion: row.latestVersion,
      minVersion: row.minVersion,
      baseBefore: Global.base.useUrl,
    });

    if (apiUrl) Global.base.useUrl = apiUrl;

    policy = {
      latestVersion: row.latestVersion,
      minVersion: row.minVersion,
      forceUpdate: !!row.forceUpdate,
      storeUrl: row.storeUrl || '',
      apiBaseUrl: apiUrl,
    };
    Commons.Storages('set', 'clientVersionPolicy', policy);

    logApiUrl('버전정책 ON 완료', {
      finalBaseUrl: Global.base.useUrl,
      policyApiBaseUrl: policy.apiBaseUrl,
    });
  } catch (e) {
    console.warn('[ApiUrl] CheckClientVersion 실패', e?.message || e);
    const cached = Commons.Storages('get', 'clientVersionPolicy');
    if (cached?.apiBaseUrl) {
      Global.base.useUrl = cached.apiBaseUrl.endsWith('/')
        ? cached.apiBaseUrl
        : `${cached.apiBaseUrl}/`;
      policy = cached;
      logApiUrl('캐시 policy 적용', {
        cachedApiBaseUrl: cached.apiBaseUrl,
        finalBaseUrl: Global.base.useUrl,
      });
    } else {
      logApiUrl('실패 후 baseUrl 유지', { finalBaseUrl: Global.base.useUrl });
    }
  }

  // OTA — 받아 둔 새 번들이 있으면 로딩 화면 중에 적용(앱 재시작, 이후 코드 실행 안 됨). 다운로드는 기다리지 않음
  await handleLiveUpdate(ota, { atStartup: true });

  return policy;
}

/** 앱으로 돌아왔을 때 OTA 재확인 (30분 간격) — 새 번들은 받아만 두고 적용은 다음 실행 때 */
export async function recheckLiveUpdate() {
  if (!isLiveUpdateEnabled()) return;
  if (Date.now() - lastLiveUpdateCheckAt < LIVE_UPDATE_RECHECK_MS) return;

  const base = getEnvBaseUrl();
  if (!base) return;

  lastLiveUpdateCheckAt = Date.now();
  try {
    const { platform, currentVersion, buildNo } = await getClientVersionInfo();
    const row = await postClientVersionCheck(
      `${base}${Global.urls.Account.CheckClientVersion}`,
      {
        platform,
        currentVersion,
        buildNo,
        ...(await getLiveUpdateRequestInfo()),
      }
    );
    await handleLiveUpdate(row.ota);
  } catch (e) {
    console.warn('[LiveUpdate] 앱 복귀 시 확인 실패', e?.message || e);
  }
}

/** 앱 복귀(포그라운드)마다 recheckLiveUpdate — 해제 함수 반환 */
export function watchLiveUpdateOnResume() {
  if (!isLiveUpdateEnabled()) return () => {};

  const listener = App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) recheckLiveUpdate();
  });

  return () => {
    listener.then((handle) => handle.remove()).catch(() => {});
  };
}
